import React, { ReactNode } from "react";
import { IDropdownOption } from "../../../interfaces/IDropdownOption";
import cx from "classnames";
import styles from "./Dropdown.module.scss";
import ValidationMessage from "../ValidationMessage/ValidationMessage";
import { PaymentFrequency } from "../../../interfaces/enums/PaymentFrequency";

interface IDropdown {
    id?: string;
    name: string;
    title?: string;
    selectedOptionId?: string;
    variant?: "default" | "paymentfreq-short" | "paymentfreq-full" | "product-sort";
    className?: string;
    placeholder?: string;
    options: IDropdownOption[];
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onBlur?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    isDisabled?: boolean;
    tooltip?: ReactNode;
    hasError?: string | boolean;
    // Would be good to default this to be true. If we need a margin it should be set via 'className'
    noMargin?: boolean;
    inlineLabel?: string | React.ReactNode;
    ref?: any;
}

const Dropdown: React.FC<IDropdown> = (props) => {
    const {
        id,
        name,
        title,
        selectedOptionId,
        variant,
        placeholder,
        options,
        isDisabled,
        onChange,
        onBlur,
        children,
        tooltip,
        hasError,
        noMargin,
        className,
        inlineLabel
    } = props;

    const paymentFreqArrowClass = (): string => {
        if (variant !== "paymentfreq-full" && variant !== "paymentfreq-short") return "";

        switch (selectedOptionId) {
            case PaymentFrequency.Fortnightly:
                return styles["fortnight-dropdown-arrow-offset"];
            case PaymentFrequency.Monthly:
                return styles["month-dropdown-arrow-offset"];
            case PaymentFrequency.Yearly:
                return styles["year-dropdown-arrow-offset"];
            default:
                return "";
        }
    };

    return (
        <div
            className={cx({
                ["gap-ml"]: !noMargin,
                [className]: !!props.className,
                [styles["dropdown-wrapper"]]: !!inlineLabel,
                [styles["dropdown--product-sort"]]: variant === "product-sort",
                [styles["dropdown--paymentfreq"]]:
                    variant === "paymentfreq-short" || variant === "paymentfreq-full",
                [styles["dropdown--paymentfreq--short"]]: variant === "paymentfreq-short",
                [styles["dropdown--paymentfreq--full"]]: variant === "paymentfreq-full"
            })}>
            {props.title && (
                <label
                    htmlFor={id || name}
                    className={cx("gap-m", {
                        [styles["dropdown-title--error"]]: hasError
                    })}>
                    {title}
                </label>
            )}
            <div className={cx(["layout-flex-row--centered", { ["gap-m"]: !noMargin }])}>
                {inlineLabel && (
                    <label
                        htmlFor={id || name}
                        className={cx("space-pre-m", {
                            "space-m": !!tooltip
                        })}>
                        {inlineLabel}
                    </label>
                )}
                <select
                    id={id || name}
                    name={name}
                    className={cx(
                        {
                            [styles["dropdown--error"]]: hasError,
                            [styles["dropdown--inline-label"]]: !!inlineLabel,
                            "space-m": !!tooltip
                        },
                        paymentFreqArrowClass()
                    )}
                    value={selectedOptionId || ""}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={props?.ref || null}
                    disabled={isDisabled}>
                    {placeholder && (
                        <option disabled value="">
                            {placeholder}
                        </option>
                    )}
                    {options.map((option) => (
                        <option value={option.value} key={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {tooltip}
            </div>
            {hasError && typeof hasError === "string" && (
                <ValidationMessage>{hasError}</ValidationMessage>
            )}
            {children}
        </div>
    );
};

export default Dropdown;
